import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';

const TripCostingPage = () => {
  return (
    <>
      <SEOHead
        title="Trip Costing & Profitability - Fleet Management Saudi Arabia"
        description="Calculate accurate trip costs, track fuel expenses, and maximize profitability for every shipment in Saudi Arabia. VAT-compliant with automated cost allocation."
        keywords="trip costing Saudi Arabia, fleet profitability, fuel cost tracking, driver settlements, maintenance costs, VAT management KSA, shipment costing"
        canonicalUrl="/trip-costing"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Trip Costing & Profitability Analysis",
          "description": "Advanced trip costing solution for Saudi Arabia fleet operators",
          "provider": {
            "@type": "Organization",
            "name": "Logistiat"
          },
          "serviceType": "Fleet Management Software",
          "areaServed": "Saudi Arabia"
        }}
      />
      <main className="min-h-screen">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div className="flex items-center">
                <Link to="/" className="text-2xl font-bold text-gray-900">Logistiat</Link>
              </div>
              <div className="hidden md:flex space-x-8">
                <Link to="/features" className="text-gray-600 hover:text-gray-900">Features</Link>
                <Link to="/pricing" className="text-gray-600 hover:text-gray-900">Pricing</Link>
                <Link to="/trip-costing" className="text-blue-600 font-medium">Trip Costing</Link>
                <Link to="/contact" className="text-gray-600 hover:text-gray-900">Contact</Link>
                <Link to="/ar" className="text-gray-600 hover:text-gray-900">العربية</Link>
              </div>
              <div className="flex space-x-4">
                <Link
                  to="/login"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 font-medium"
                >
                  Login
                </Link>
              </div>
            </div>
          </nav>
        </header>

        {/* Hero Section */}
        <section className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Trip Costing & Profitability
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
              Calculate accurate trip costs, track fuel expenses, and maximize profitability for every shipment in Saudi Arabia.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/login"
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Start Calculating Costs
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
              >
                Request Demo
              </Link>
            </div>
          </div>
        </section>

        {/* Trip Costing Features */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Complete Trip Cost Management
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Track every expense, calculate accurate costs, and optimize your fleet profitability
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="text-4xl mb-4">⛽</div>
                <h3 className="text-xl font-semibold mb-4">Fuel Cost Tracking</h3>
                <p className="text-gray-600">
                  Automatic fuel cost calculation with PetroApp integration. Track fuel consumption, costs per kilometer, and fuel efficiency metrics.
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="text-4xl mb-4">💰</div>
                <h3 className="text-xl font-semibold mb-4">Driver Settlements</h3>
                <p className="text-gray-600">
                  Calculate driver payments, commissions, and settlements automatically. Track driver expenses and performance metrics.
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="text-4xl mb-4">🔧</div>
                <h3 className="text-xl font-semibold mb-4">Maintenance Costs</h3>
                <p className="text-gray-600">
                  Track vehicle maintenance expenses, schedule preventive maintenance, and calculate maintenance cost per kilometer.
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="text-4xl mb-4">📊</div>
                <h3 className="text-xl font-semibold mb-4">Profitability Analysis</h3>
                <p className="text-gray-600">
                  Real-time profitability analysis for each trip. Compare actual vs estimated costs and identify optimization opportunities.
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="text-4xl mb-4">🧾</div>
                <h3 className="text-xl font-semibold mb-4">VAT & Tax Management</h3>
                <p className="text-gray-600">
                  Automatic VAT calculations compliant with Saudi Arabia tax regulations. Generate tax reports and maintain compliance.
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="text-4xl mb-4">📈</div>
                <h3 className="text-xl font-semibold mb-4">Cost Analytics</h3>
                <p className="text-gray-600">
                  Advanced analytics and reporting on trip costs, fuel efficiency, and fleet performance. Make data-driven decisions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Cost Calculation Process */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                How Trip Costing Works
              </h2>
              <p className="text-xl text-gray-600">
                Simple 4-step process to calculate accurate trip costs
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
                <h3 className="text-xl font-semibold mb-2">Create Shipment</h3>
                <p className="text-gray-600">Enter shipment details, route, and vehicle information</p>
              </div>

              <div className="text-center">
                <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
                <h3 className="text-xl font-semibold mb-2">Track Expenses</h3>
                <p className="text-gray-600">Automatic fuel tracking and manual expense entry</p>
              </div>

              <div className="text-center">
                <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
                <h3 className="text-xl font-semibold mb-2">Calculate Costs</h3>
                <p className="text-gray-600">Real-time cost calculation with VAT and taxes</p>
              </div>

              <div className="text-center">
                <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">4</div>
                <h3 className="text-xl font-semibold mb-2">Generate Reports</h3>
                <p className="text-gray-600">Detailed cost reports and profitability analysis</p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Maximize Your Fleet Profitability
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="bg-green-100 p-2 rounded-lg mr-4">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Reduce Operating Costs</h3>
                      <p className="text-gray-600">Identify cost-saving opportunities and optimize fuel consumption</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="bg-green-100 p-2 rounded-lg mr-4">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Accurate Pricing</h3>
                      <p className="text-gray-600">Set competitive prices based on real cost data</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="bg-green-100 p-2 rounded-lg mr-4">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Improved Cash Flow</h3>
                      <p className="text-gray-600">Better financial planning with accurate cost forecasting</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-green-50 p-8 rounded-2xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Cost Savings Example</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monthly Fuel Costs:</span>
                    <span className="font-semibold">50,000 SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Optimization Savings (10%):</span>
                    <span className="font-semibold text-green-600">-5,000 SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Maintenance Optimization:</span>
                    <span className="font-semibold text-green-600">-3,000 SAR</span>
                  </div>
                  <hr className="border-gray-300" />
                  <div className="flex justify-between text-lg">
                    <span className="font-semibold">Monthly Savings:</span>
                    <span className="font-bold text-green-600">8,000 SAR</span>
                  </div>
                  <div className="flex justify-between text-xl">
                    <span className="font-bold">Annual Savings:</span>
                    <span className="font-bold text-green-600">96,000 SAR</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Start Optimizing Your Trip Costs Today
            </h2>
            <p className="text-xl mb-8">
              Join hundreds of Saudi trucking companies using Logistiat for accurate trip costing and improved profitability.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/login"
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Start Free Trial
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
              >
                Schedule Demo
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">Logistiat</h3>
                <p className="text-gray-400">
                  Complete fleet management solution for Saudi Arabia trucking companies.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-4">Features</h4>
                <ul className="space-y-2 text-gray-400">
                  <li><Link to="/features" className="hover:text-white">Fleet Management</Link></li>
                  <li><Link to="/trip-costing" className="hover:text-white">Trip Costing</Link></li>
                  <li><Link to="/features" className="hover:text-white">Driver Management</Link></li>
                  <li><Link to="/features" className="hover:text-white">Fuel Tracking</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-4">Company</h4>
                <ul className="space-y-2 text-gray-400">
                  <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
                  <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
                  <li><Link to="/ar" className="hover:text-white">العربية</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-4">Support</h4>
                <ul className="space-y-2 text-gray-400">
                  <li><Link to="/contact" className="hover:text-white">Help Center</Link></li>
                  <li><Link to="/contact" className="hover:text-white">Contact Support</Link></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
              <p>&copy; 2024 Logistiat. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </main>
    </>
  );
};

export default TripCostingPage;